import {FC, useEffect, useRef, useState} from 'react';
import {Document, Page, pdfjs} from 'react-pdf';
import {
    DocumentCallback,
    File,
    PageCallback,
} from 'react-pdf/dist/cjs/shared/types';
import clsx from 'clsx';
import Spinner from 'components/Loaders/Spinner';
import ControlPanel from './ControlPanel';
import {fitWidth} from './ControlPanel/utils';

type PDFReaderProps = {
    file: File;
    showAllPages?: boolean;
};

const PDFReader: FC<PDFReaderProps> = ({file, showAllPages = true}) => {
    const [scale, setScale] = useState(1);
    const [numberPages, setNumberPages] = useState(1);
    const [pageNumber, setPageNumber] = useState(1);
    const [renderedPageNumber, setRenderedPageNumber] = useState<number | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState(true);
    const [pageOriginalWidth, setPageOriginalWidth] = useState(0);
    const containerInner = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
    }, []);

    const onDocumentLoadSuccess = ({numPages}: DocumentCallback) => {
        setNumberPages(numPages);

        if (showAllPages) {
            setPageNumber(numPages);
        }
        setIsLoading(false);
    };

    const onPageLoadSuccess = ({originalWidth}: PageCallback) => {
        if (pageNumber !== renderedPageNumber) {
            setScale(
                fitWidth(
                    originalWidth,
                    containerInner.current?.clientWidth || 0,
                ),
            );
            setPageOriginalWidth(originalWidth);
        }
        setRenderedPageNumber(pageNumber);
    };

    return (
        <div className="p-2 border bg-grey-200 border-grey-300 rounded-md w-full h-full relative flex flex-col">
            {isLoading && (
                <div className="absolute w-full top-0 left-0 h-full z-10 bg-grey-200 flex items-center justify-center">
                    <Spinner className="m-auto" size="xl" />
                </div>
            )}
            {!showAllPages && (
                <ControlPanel
                    containerRef={containerInner}
                    numPages={numberPages}
                    pageNumber={pageNumber}
                    pageWidth={pageOriginalWidth}
                    scale={scale}
                    setPageNumber={setPageNumber}
                    setScale={setScale}
                />
            )}
            <div
                ref={containerInner}
                className={clsx(
                    'w-full flex-1 overflow-x-hidden',
                    numberPages > 1 ? 'overflow-y-auto' : 'overflow-y-hidden',
                )}
            >
                <Document
                    className="flex-1 flex flex-col"
                    error={false}
                    file={file}
                    loading={false}
                    onLoadSuccess={onDocumentLoadSuccess}
                >
                    {showAllPages ? (
                        <>
                            {Array.from({length: pageNumber}, (_, index) => (
                                <Page
                                    key={`page_${index + 1}`}
                                    className="mb-2 last:mb-0 mx-auto bg-transparent"
                                    error={false}
                                    loading={false}
                                    onLoadSuccess={onPageLoadSuccess}
                                    pageNumber={index + 1}
                                    renderAnnotationLayer={false}
                                    renderTextLayer={false}
                                    scale={scale}
                                />
                            ))}
                        </>
                    ) : (
                        <Page
                            key={`page_${pageNumber}@${scale}`}
                            className="mx-auto bg-transparent"
                            error={false}
                            loading={false}
                            onLoadSuccess={onPageLoadSuccess}
                            pageNumber={pageNumber}
                            renderAnnotationLayer={false}
                            renderTextLayer={false}
                            scale={scale}
                        />
                    )}
                </Document>
            </div>
        </div>
    );
};

export default PDFReader;
